import React, { useState, useEffect } from 'react';
import { RefreshCcw, Users, Edit, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import StatCard from '@/components/common/StatCard/StatCard';

const UserAdminPanel = () => {
    const { success, error } = useNotifications();
    
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(1);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchUsers();
        fetchCategories();
    }, []);

    const fetchUsers = async (page = 1) => {
        try {
            const token = localStorage.getItem('token');

            const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/admin/users?page=${page}`,
            {
                headers: { Authorization: `Bearer ${token}` },
            }
            );

            if (!response.ok) return;

            const data = await response.json();
            setUsers(data.users);
            setPage(data.page);
            setTotal(data.total);
            setTotalPages(data.totalPages);
            setLoading(false);
        } catch (err) {
            error("Error cargando usuarios");
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories`, {
            headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setCategories(data);
        } catch (err) {
            error("Error cargando categorías");
        }
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const updateUser = async (updatedUser) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/users/${updatedUser._id}`, {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(updatedUser)
            });

            if (response.ok) {
                success("Usuario actualizado");
                fetchUsers();
                closeModal();
            } else {
                error("Error actualizando el usuario");
            }
        } catch (err) {
            error("Error al actualizar el usuario");
        }
    };

    return (
        <div className="space-y-8">

            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl p-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Users /> Panel de Usuarios
                    </h1>

                    <StatCard
                        title="Usuarios"
                        value={users.length}
                        icon={<Users />}
                        color="blue"
                    />

                    <button
                        onClick={fetchUsers}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <RefreshCcw size={18} /> Actualizar
                    </button>
                </div>
            </div>

            {/* Tabla de usuarios */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
                {loading ? (
                    <div className="text-center py-16 text-gray-500">Cargando usuarios...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="border-b">
                                    <th className="px-4 py-2 text-left">Nombre</th>
                                    <th className="px-4 py-2 text-left">Email</th>
                                    <th className="px-4 py-2 text-left">Teléfono</th>
                                    <th className="px-4 py-2 text-left">Rol</th>
                                    <th className="px-4 py-2 text-left">Categorías</th>
                                    <th className="px-4 py-2">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user._id} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2 text-left">{user.name}</td>
                                        <td className="px-4 py-2 text-left">{user.email}</td>
                                        <td className="px-4 py-2 text-left">{user.phone || '-'}</td>
                                        <td className="px-4 py-2 text-left">{user.role}</td>
                                        <td className="px-4 py-2 text-left">
                                            {(user.role === "admin" || user.role === "gestor")
                                                ? (user.scopes?.join(", ") || "-")
                                                : "-"}
                                        </td>
                                        <td className="px-4 py-2">
                                            <button
                                                onClick={() => openEditModal(user)}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                            >
                                                <Edit size={16} /> Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex justify-between items-center mt-6">
                            <button
                                disabled={page <= 1}
                                onClick={() => fetchUsers(page - 1)}
                                className={`px-4 py-2 rounded-lg flex row items-center ${
                                    page <= 1 
                                        ? "bg-gray-300 cursor-not-allowed" 
                                        : "bg-gray-300 hover:bg-gray-300"
                                }`}
                            >
                                 <ArrowLeft /> Anterior
                            </button>

                            <span className="text-gray-700">
                                Página {page} de {totalPages}
                            </span>

                            <button
                                disabled={page >= totalPages}
                                onClick={() => fetchUsers(page + 1)}
                                className={`px-4 py-2 rounded-lg flex row items-center ${
                                    page >= totalPages
                                        ? "bg-gray-300 cursor-not-allowed"
                                        : "bg-gray-300 hover:bg-gray-300"
                                }`}
                            >
                                Siguiente <ArrowRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <EditUserModal 
                    user={selectedUser}
                    categories={categories}
                    onClose={closeModal} 
                    onSave={updateUser}
                />
            )}
        </div>
    );
};

const EditUserModal = ({ user, categories, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        ...user,
        scopes: user.scopes || []
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const toggleScope = (scope) => {
        setFormData(prev => ({
            ...prev,
            scopes: prev.scopes.includes(scope)
            ? prev.scopes.filter(s => s !== scope)
            : [...prev.scopes, scope]
        }));
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-xl w-196 min-w-[450px]">
                <h3 className="text-xl font-semibold mb-4">Editar Usuario</h3>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700">Nombre</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border rounded-lg mt-1"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Rol</label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border rounded-lg mt-1"
                        >
                            <option value="cliente">Cliente</option>
                            <option value="admin">Admin</option>
                            <option value="gestor">Gestor</option>
                        </select>
                    </div>
                    {(formData.role === "admin" || formData.role === "gestor") && (
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Categorías</label>

                            <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                            {categories.map(cat => (
                                <label key={cat._id} className="flex items-center gap-2 py-1">
                                <input
                                    type="checkbox"
                                    checked={formData.scopes.includes(cat.slug)}
                                    onChange={() => toggleScope(cat.slug)}
                                />
                                {cat.name}
                                </label>
                            ))}
                            </div>
                        </div>
                    )}
                    <div className="mb-4">
                        <label className="block text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border rounded-lg mt-1"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">Teléfono</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border rounded-lg mt-1"
                        />
                    </div>
                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                        >
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserAdminPanel;